import { Component, ViewEncapsulation, ViewChild, OnInit } from '@angular/core';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { PopoverDirective } from 'ngx-bootstrap/popover';
import { BaseModalComponent } from '../../../models/base-modal-component';
import * as _ from 'underscore';
import { DrObject, DrObjectType, SupportedRegExType } from '../../../models/dr-object';
import { Endpoint, HelpIndex, OperationType, StorageType } from '../../../models/enums';
import { RestService } from '../../../services/rest/rest.service';
import { NameDescriptionComponent } from '../../name-description/name-description.component';
import { NgForm } from '@angular/forms';
import { MountedConnectorMode } from '../../../models/connector';

export enum StorageUseType {
    DOCUMENT_STORE = 'DOCUMENT_STORE',
    INDEX_STORE = 'INDEX_STORE'
}

@Component({
    standalone: false,
    selector: 'res-storage-modal',
    templateUrl: './storage-modal.component.html',
    styleUrls: ['./storage-modal.component.scss'],
    encapsulation: ViewEncapsulation.None
})

export class StorageModalComponent extends BaseModalComponent implements OnInit {
    @ViewChild(NameDescriptionComponent, { static: true }) nameDescriptionComponent: NameDescriptionComponent;
    @ViewChild('errorPopover', { static: true }) errorPopover: PopoverDirective;
    @ViewChild('contentForm') contentForm: NgForm;
    @ViewChild('validationPop') validationPop: PopoverDirective;
    debounceNodeOrHost = _.debounce(this.nodeOrHostChanged, 2000);

    validateNameRequest = {
        endpoint: Endpoint.viewManager_validateName,
        request: (name) => {
            return {
                name: this.nameDescriptionComponent.name,
                objectType: DrObjectType.STORAGE,
                systemScope: true
            };
        }
    };
    StorageType = StorageType;

    modalTitle = 'New Storage';
    primaryButtonLabel = 'OK';
    secondaryButtonLabel = 'Cancel';
    isBusy = false;
    isBusyMessage = 'Loading...';
    modalClass = 'storage-modal';
    modalAutomationId = 'new-edit-storage-modal';
    modalHelpIndex = HelpIndex.sys_add_edit_storage_dlg;
    storageType: StorageType;
    nodeOrHostLabel: string;
    fileSystemProtocol = 'NFS';
    allNodeOrHostNames: any[];
    allNodesOrHosts: any[];
    allStoragePaths: [];
    selectedNodeOrHost;
    selectedStoragePath;
    callInProgress: boolean;
    isValid: boolean;
    isEditStorage: boolean
    isRemote: boolean
    isInUse: boolean;
    tooltipWhenDisabled = '';
    disableIndexTab = false;
    isDirty = false;

    constructor(
        private modalService: BsModalService,
        private modalRef: BsModalRef,
        private restService: RestService) {
        super(modalService, modalRef);
    }

    ngOnInit(): void {
        this.nameDescriptionComponent.drObject = new DrObject();
        this.nameDescriptionComponent.drObject.regex = SupportedRegExType.CONNECTOR;
        this.isEditStorage = this.modalData.storageOperationType === OperationType.edit;
        if (!this.isEditStorage) {
            this.modalTitle = 'New Storage';
            this.disableIndexTab = this.modalData?.indexStorageExists;
            this.setType(StorageType.Document);
        } else {
            this.modalTitle = 'View / Edit Storage';
            this.nameDescriptionComponent.name = this.modalData.storage.name;
            this.nameDescriptionComponent.formValid = true;
            this.selectedNodeOrHost = this.modalData.storage.fqdn;
            this.selectedStoragePath = this.modalData.storage.export;
            this.fileSystemProtocol = this.modalData.storage.facilityType === 'PANFS_NAS' ? 'PANFS' : 'NFS';
            this.isInUse = this.modalData.isInUse;
            this.setType(this.modalData.storage.storageUseType === StorageUseType.INDEX_STORE ? StorageType.Index : StorageType.Document);
            if (!this.isInUse) {
                this.restService.post(Endpoint.orgManager_getNfsMounts, {
                    remoteHost: this.selectedNodeOrHost,
                    timeoutSeconds: 10,
                    contextHandle: 'super_system_customer'
                }).then(response => {
                    this.allStoragePaths = response.mounts;
                })
            }
        }
        this.callInProgress = false;
    }

    loadNodes() {
        this.restService.post(Endpoint.realmManager_listNodes, { contextHandle: 'super_system_customer' }).then(response => {
            if (response?.nodes?.length > 0) {
                this.allNodesOrHosts = response.nodes;
                this.allNodeOrHostNames = this.allNodesOrHosts.map(q => q.name);;
            }
        })
    }

    okEnabled(): boolean {
        return !this.isBusy && !DrObject.isEmpty(this.nameDescriptionComponent.name) && this.nameDescriptionComponent.formValid
            && this.isValid && (!this.isEditStorage || (this.isEditStorage && !this.isInUse && this.isDirty && !this.callInProgress));
    }

    validateEnabled(): boolean {
        return !this.isBusy && this.selectedNodeOrHost && this.selectedStoragePath;
    }

    ok(): void {
        if (this.isValid) {
            this.isBusy = true;
            this.isBusyMessage = 'Saving ...';
            const selectedFacilityType = this.getFacilityTypeFromDisplayType(this.fileSystemProtocol);
            if (this.isEditStorage) {
                const requestData = {
                    allocationSize: 0,
                    export: this.selectedStoragePath,
                    facilityType: selectedFacilityType,
                    requestHandle: this.modalData.storage.handle,
                    fqdn: this.selectedNodeOrHost,
                    storageAreaType: 'DOC_STORAGE',
                    storageUseType: this.modalData.storage.storageUseType,
                    contextHandle: 'super_system_customer'
                }
                this.restService.post(Endpoint.storageAreaManager_updateRemoteNFSStorageArea, requestData).then(response => {
                    this.isBusy = false;
                    if (response?.remoteStorageArea) {
                        this.closeModal(true, response.remoteStorageArea);
                    }
                }, () => {
                    this.isBusy = false;
                });
            } else {
                const requestData = {
                    allocationSize: 0,
                    export: this.selectedStoragePath,
                    facilityType: selectedFacilityType,
                    name: this.nameDescriptionComponent.name,
                    fqdn: this.selectedNodeOrHost,
                    monitoringNode: null,
                    storageAreaType: 'DOC_STORAGE',
                    storageUseType: this.storageType === StorageType.Document ? StorageUseType.DOCUMENT_STORE : StorageUseType.INDEX_STORE,
                    contextHandle: 'super_system_customer',
                    inUse: true
                }

                this.restService.post(Endpoint.realmManager_createRemoteNFSStorageArea, requestData).then(response => {
                    this.isBusy = false;
                    if (response?.remoteStorageArea) {
                        this.closeModal(true, response.remoteStorageArea);
                    }
                }, () => {
                    this.isBusy = false;
                });
            }
        }
    }

    getFacilityTypeFromDisplayType(fileSystemProtocolDisplayed: string): string {
        return fileSystemProtocolDisplayed === 'NFS' ? 'NFS_NAS' : 'PANFS_NAS';
    }

    setType(type) {
        this.storageType = type;
        this.hideValidationPopup();
        this.allNodesOrHosts = [];
        this.allNodeOrHostNames = [];
        if (!this.isEditStorage) {
            this.selectedNodeOrHost = '';
            this.selectedStoragePath = '';
            this.fileSystemProtocol = 'NFS';
        }
        if (type === StorageType.Document) {
            this.isRemote = false;
            if (!this.isEditStorage) {
                this.loadNodes();
            }
        } else {
            this.isRemote = true;
        }
    }

    onNodeOrHostChanged = (item: any): void => {
        this.debounceNodeOrHost(item?.currentTarget?.value);
    }

    onStoragePathChanged = (item: any): void => {
        if (this.selectedStoragePath !== item) {
            this.isValid = false;
            this.selectedStoragePath = item;
            this.hideValidationPopup();
        }
    }

    nodeOrHostChanged(item: any): void {
        this.isValid = false;
        this.hideValidationPopup();
        this.selectedNodeOrHost = item;
        this.callInProgress = true;
        const selectedItem = this.allNodesOrHosts.filter(item => item.name === this.selectedNodeOrHost)
        this.allStoragePaths = [];
        this.selectedStoragePath = '';
        const request = {
            remoteHost: '',
            timeoutSeconds: 10,
            contextHandle: 'super_system_customer'
        };
        if (selectedItem?.length > 0) {
            request.remoteHost = selectedItem[0].ipAddress;
        } else {
            request.remoteHost = this.selectedNodeOrHost;
        }
        this.restService.post(Endpoint.orgManager_getNfsMounts, request).then(response => {
            this.allStoragePaths = response.mounts;
            this.callInProgress = false;
        }, () => {
            this.callInProgress = false;
        })
    }
    dropdownDisabled() {
        const nodeOrHostNotReady = !this.selectedNodeOrHost || this.callInProgress;
        this.tooltipWhenDisabled = 'Select or enter "IP Address / Host Name" first ...';
        return nodeOrHostNotReady || this.elementsDisabled();
    }

    elementsDisabled() {
        return this.isEditStorage && this.isInUse;
    }

    onFocusChanged = (item: any): void => {
        return item;
    }

    validate() {
        this.isBusy = true;
        this.isBusyMessage = 'Validating ... ';
        this.restService.post(Endpoint.connectorManager_validateNFSConnector, {
            mountedConnectorMode: this.fileSystemProtocol === 'PANFS' ? MountedConnectorMode.PANFS : MountedConnectorMode.CLASSIC,
            remoteHost: this.selectedNodeOrHost,
            remotePath: this.selectedStoragePath,
            readOnly: true,
            systemScope: true
        }).then((validateReponse) => {
            this.isValid = validateReponse ? validateReponse.valid : false;
            this.validationPop.containerClass = this.isValid ? 'success' : 'error';
            this.validationPop.show();
            this.isBusy = false;
        }, () => {
            this.isBusy = false;
            this.isValid = false;
            this.validationPop.containerClass = 'error';
            this.validationPop.show();
        })
    }

    hideValidationPopup() {
        if (this.validationPop?.isOpen) {
            this.validationPop.hide();
        }
        if (this.isEditStorage && !this.isInUse && this.modalData?.storage) {
            this.isDirty = (this.getFacilityTypeFromDisplayType(this.fileSystemProtocol) !== this.modalData.storage.facilityType) || (this.selectedNodeOrHost !== this.modalData.storage.fqdn) || (this.selectedStoragePath !== this.modalData.storage.export);
            if (this.isDirty && this.isValid) {
                this.isValid = false;
            }
        }
    }
}
